import { useState } from "react";
import { useNavigate } from "react-router";
import Cookies from "js-cookie";
import { apiClient } from "@/lib/api-client";
import { ROLE_REDIRECT_MAP } from "@/config/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LockKeyhole, Loader2 } from "lucide-react";

interface LoginResponse {
  expires_at: number;
  message: string;
  token: string;
}

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await apiClient<LoginResponse>(
        "/api/v1/auth/admin/login",
        {
          method: "POST",
          body: JSON.stringify({ username, password }),
        },
      );

      // Save token to cookie
      const expires = new Date(response.expires_at * 1000);
      Cookies.set("auth_token", response.token, { expires, path: "/" });

      // Fetch user profile to determine role
      const profileResponse = await apiClient<any>("/api/v1/admin/profile");
      const role = profileResponse.jenis;

      const redirectPath =
        ROLE_REDIRECT_MAP[role as keyof typeof ROLE_REDIRECT_MAP] || "/";
      navigate(redirectPath);
    } catch (err: any) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex-1 flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950 overflow-hidden selection:bg-blue-200 selection:text-blue-900">
      {/* Decorative Background Elements */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-96 w-96 rounded-full bg-blue-500/20 blur-[100px] dark:bg-blue-600/20" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-indigo-500/20 blur-[100px] dark:bg-indigo-600/20" />

      <div className="relative z-10 w-full max-w-105 px-4 sm:px-0">
        <div className="rounded-3xl border border-white/40 bg-white/70 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80 sm:p-10">
          <div className="mb-8 flex flex-col items-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-linear-to-tr from-blue-600 to-indigo-500 text-white shadow-lg shadow-blue-500/30">
              <LockKeyhole className="h-7 w-7" />
            </div>
            <h2 className="text-center text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Sistem Remunerasi
            </h2>
            <p className="mt-1.5 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
              Silakan masuk ke akun Anda
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleLogin}>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label
                  htmlFor="username"
                  className="text-slate-700 dark:text-slate-300 font-medium"
                >
                  Username
                </Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Masukkan username"
                  className="h-11 rounded-xl bg-white/50 dark:bg-slate-950/50 transition-all focus:bg-white dark:focus:bg-slate-900"
                />
              </div>
              <div className="space-y-1.5">
                <Label
                  htmlFor="password"
                  className="text-slate-700 dark:text-slate-300 font-medium"
                >
                  Password
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Masukkan password"
                  className="h-11 rounded-xl bg-white/50 dark:bg-slate-950/50 transition-all focus:bg-white dark:focus:bg-slate-900"
                />
              </div>
            </div>

            {error && (
              <div className="animate-in fade-in slide-in-from-top-1 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="mt-2 w-full h-11 rounded-xl bg-linear-to-r from-blue-600 to-indigo-600 text-base font-semibold text-white shadow-md transition-all hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Memproses...
                </>
              ) : (
                "Masuk"
              )}
            </Button>
          </form>
        </div>

        <p className="mt-8 text-center text-xs font-medium text-slate-500 dark:text-slate-500">
          &copy; {new Date().getFullYear()} Universitas Pendidikan Ganesha
        </p>
      </div>
    </div>
  );
}
